import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Linkedin, Instagram, Facebook, Youtube } from "lucide-react";
import { COMPANY_EMAIL, COMPANY_CITY, serviceList } from "../data/mock";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

const popularSlugs = [
  "ota-management",
  "channel-management",
  "room-pricing",
  "hotel-crm",
  "whatsapp-automation",
  "social-media",
  "website-development",
];

const socials = [
  { key: "social_linkedin", icon: Linkedin, href: "https://www.linkedin.com/company/superhostcloud", label: "LinkedIn" },
  { key: "social_instagram", icon: Instagram, href: "https://www.instagram.com/superhostcloud", label: "Instagram" },
  { key: "social_facebook", icon: Facebook, href: "https://www.facebook.com/superhostcloud", label: "Facebook" },
  { key: "social_youtube", icon: Youtube, href: "https://www.youtube.com/@superhostcloud", label: "YouTube" },
];

export default function Footer() {
  const { t, lang } = useLanguage();
  const { contact, services_meta, phones } = usePublicContent();
  const navigate = useNavigate();
  const location = useLocation();
  const email = contact?.email || COMPANY_EMAIL;
  const city = (lang === "hi" ? (contact?.city_hi || contact?.city_en) : contact?.city_en) || COMPANY_CITY;
  const svcMap = {};
  (services_meta || []).forEach((x) => { svcMap[x.slug] = x; });
  const popular = popularSlugs
    .map((slug) => serviceList.find((s) => s.slug === slug))
    .filter(Boolean)
    .filter((s) => svcMap[s.slug]?.active !== false);

  const goToWhy = () => {
    if (location.pathname !== "/") {
      navigate("/#why-choose-us");
    } else {
      document.getElementById("why-choose-us")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <span className="inline-block bg-white rounded-xl px-4 py-3 shadow-sm">
              <img src="/superhost-logo-transparent.png" alt="Superhost Cloud" className="h-11 w-auto" />
            </span>
            <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">{t.footer.company}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/services" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.nav.services}</Link></li>
              <li>
                <button
                  onClick={goToWhy}
                  data-testid="footer-why-choose-us"
                  className="text-left text-white/70 hover:text-[#E84B1C] transition-colors"
                >
                  {t.nav.why}
                </button>
              </li>
              <li><Link to="/contact" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.nav.contact}</Link></li>
              <li><Link to="/privacy-policy" data-testid="footer-privacy-policy" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.footer.privacy}</Link></li>
              <li><Link to="/terms-and-conditions" data-testid="footer-terms-conditions" className="text-white/70 hover:text-[#E84B1C] transition-colors">{t.footer.terms}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">{t.footer.popularServices}</h4>
            <ul className="space-y-3 text-sm">
              {popular.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={s.detail ? `/services/${s.slug}` : "/services"}
                    className="text-white/70 hover:text-[#E84B1C] transition-colors"
                  >
                    {t.services[s.slug].title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">{t.footer.getInTouch}</h4>
            <p className="font-head font-bold text-white text-lg mb-3">{t.footer.brand}</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3 text-white/70">
                <Phone size={17} className="text-[#E84B1C] shrink-0" />
                <span className="flex flex-wrap gap-x-2">
                  {phones.map((p, i) => (
                    <a key={p.tel} href={p.tel} className="hover:text-white transition-colors" data-testid={`footer-phone-${i + 1}`}>
                      {p.display}{i < phones.length - 1 ? "," : ""}
                    </a>
                  ))}
                </span>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Mail size={17} className="text-[#E84B1C] shrink-0" /> {email}
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <MapPin size={17} className="text-[#E84B1C]" /> {city}
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                const href = (contact?.[s.key] ?? s.href).trim();
                if (!href) return null;
                return (
                  <a
                    key={s.label}
                    href={href}
                    data-testid={`footer-${s.key}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#E84B1C] flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Superhost Cloud. {t.footer.rights}
          </p>
          <p className="text-white/50 text-sm">{t.footer.madeIn}</p>
        </div>
      </div>
    </footer>
  );
}
